import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useCheckBox, useSecondOrderCallback, useViewport } from '@chilecompra/react-kit'
import {
  CheckBox,
  Popover,
  PopoverMenuItem,
  Skeleton,
  Toast,
  Typography,
  Tooltip
} from '@chilecompra/react-kit/components'
import { useConfig } from '../ConfigProvider/ConfigProvider.hook'
import SellersAdded from './QuoteStepTwo.sellersAdded'
import {
  Anchor,
  AnchorIcon,
  AnchorText,
  Grid,
  Paragraph,
  Strong,
  TooltipIcon,
  TooltipText
} from './QuoteStepTwo.styles'

/**
 * The Notifications's component.
 */
const Notifications = props => {
  const {
    delivery: { region: regionDefault },
    onCompanySizeFilter,
    filterRegionCode,
    regionValue,
    sellersAdded,
    showError,
    onRemoveSeller,
    setRegionValue
  } = props
  const {
    regionInfo: { regions }
  } = useConfig()

  const {
    size: { isDownToTiny }
  } = useViewport()

  const [regionName, setRegionName] = useState('Seleccionar región')
  const [regionOpen, setRegionOpen] = useState(false)
  const { authInfo } = useSelector(state => state.authorizationProvider)
  const size = 'large'

  const {
    value: checkedNotification,
    onChange: handleNotificationChange,
    setValue: setNotification
  } = useCheckBox({
    changeCallback: checked => {
      if (checked) {
        onCompanySizeFilter(0)
        setRegionValue(regionDefault?.id)
        setRegionName(regions?.find(region => region.value === regionDefault?.id)?.name)
      }
    }
  })

  const {
    value: checkedCompanySizeFilter,
    onChange: handleCompanySizeFilterChange,
    setValue: setCompanySizeFilter
  } = useCheckBox({
    changeCallback: checked => {
      if (checked) {
        onCompanySizeFilter(1, -1, 0)
        setRegionValue(-1)
        setRegionName('Seleccionar región')
      } else {
        onCompanySizeFilter(0, -1, 0)
      }
    }
  })

  const handleCloseRegion = useCallback(() => setRegionOpen(false), [])
  const handleOpenRegion = useCallback(() => setRegionOpen(true), [])

  const createHandleYearChange = useSecondOrderCallback(
    (_, updatedValue) => {
      if (updatedValue > 0) {
        setRegionValue(updatedValue)
        setRegionName(regions?.find(region => region.value === updatedValue)?.name)
        setRegionOpen(false)
      }
    },
    [regions, regionValue]
  )

  useEffect(() => {
    setNotification(false)
    setCompanySizeFilter(false)
    if (!regionDefault?.id || filterRegionCode > -1) {
      setNotification(false)
      setCompanySizeFilter(false)
      return
    }
    if (checkedCompanySizeFilter) {
      setCompanySizeFilter(false)
      setRegionValue(regionDefault?.id)
      setRegionName(regions?.find(region => region.value === regionDefault?.id)?.name)
    }
  }, [regionDefault, regions])

  return (
    <Grid container margin="8px 0 48px">
      <Grid item xs={12} sm={3}>
        <Typography color="black1" fontWeight="bold" variant="body2">
          Notificar a proveedores <br />
          priorizados
          <Tooltip
            content={
              <TooltipText>
                Los proveedores priorizados corresponden a todos los proveedores clasificados como pequeña y <br />
                mediana empresa.
              </TooltipText>
            }
          >
            <TooltipIcon size={size} />
          </Tooltip>
        </Typography>
        <Typography
          color="black1"
          fontSize="12px"
          italic
          lineHeight="14px"
          margin={isDownToTiny ? '0 0 12px' : '0'}
          variant="body2"
        >
          Los proveedores priorizados
          <br /> recibirán una notificación por correo
          <br /> electrónico.
        </Typography>
      </Grid>
      <Grid item xs={12} sm>
        <Grid container>
          <Grid item xs={12}>
            <CheckBox
              disabled={checkedNotification}
              checked={checkedCompanySizeFilter}
              label="Notificar a todos los proveedores priorizados."
              onChange={handleCompanySizeFilterChange}
            />
          </Grid>
          <Grid item xs={6}>
            <CheckBox
              disabled={checkedCompanySizeFilter}
              checked={checkedNotification}
              label="Notificar a los proveedores priorizados de"
              onChange={handleNotificationChange}
            />
          </Grid>
          <Grid item xs>
            {!regions?.length && <Skeleton height="25px" width="172px" />}
            {regions?.length > 0 && (
              <Popover
                anchor={
                  <Anchor style={{ pointerEvents: !checkedNotification ? 'none' : 'auto' }}>
                    <AnchorText active={regionValue !== 0}>{regionName}</AnchorText>
                    <AnchorIcon open={regionOpen} />
                  </Anchor>
                }
                disabled={checkedCompanySizeFilter}
                open={regionOpen}
                onClose={handleCloseRegion}
                onOpen={handleOpenRegion}
              >
                {regions?.map(region => (
                  <PopoverMenuItem
                    enableClose
                    key={region.value}
                    onClick={authInfo.hasObserverRole ? '' : createHandleYearChange(region.value)}
                  >
                    {region.name}
                  </PopoverMenuItem>
                ))}
              </Popover>
            )}
          </Grid>
        </Grid>
        {sellersAdded && sellersAdded.length > 0 && (
          <Grid container margin="32px 0 0">
            <Grid item xs={12}>
              <Typography color="black1" fontWeight="bold" variant="body2">
                Proveedores agregados
              </Typography>
            </Grid>
            {sellersAdded.map((sellerAdded, index) => (
              <SellersAdded
                businessName={sellerAdded?.name}
                divider={sellersAdded.length - 1 !== index}
                dni={sellerAdded?.dni}
                key={sellerAdded?.dni}
                onRemoveSeller={onRemoveSeller}
              />
            ))}
          </Grid>
        )}
        {showError && (
          <Grid container margin="41px 0 0">
            <Grid item xs={12}>
              <Toast severity="warning">
                <Paragraph>
                  La cantidad de proveedores que ofertan tus productos o servicios en esta región es menor a tres.
                </Paragraph>
                <Paragraph>
                  Por favor, <Strong>selecciona otra región o agrega más proveedores específicos</Strong>.
                </Paragraph>
              </Toast>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

Notifications.defaultProps = {
  delivery: {},
  companySizeFilter: 0,
  filterRegionCode: -1,
  filterRegionNotification: 0,
  regionValue: '',
  sellersAdded: [],
  showError: false,
  onCompanySizeFilter: () => undefined,
  onUpdatedStepTwoStore: () => undefined,
  onRemoveSeller: () => undefined,
  setRegionValue: () => undefined
}

Notifications.propTypes = {
  delivery: PropTypes.shape({
    region: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })
  }),
  filterRegionCode: PropTypes.number,
  regionValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sellersAdded: PropTypes.arrayOf(
    PropTypes.shape({
      dni: PropTypes.string,
      name: PropTypes.string
    })
  ),
  showError: PropTypes.bool,
  onRemoveSeller: PropTypes.func,
  setRegionValue: PropTypes.func,
  onCompanySizeFilter: PropTypes.func
}

export default Notifications
